import { useState } from 'react';
import { useQuery } from '../api.js';
import { List, Row, Tile } from '../controls.js';
import { ErrorNote, Pill, Spinner } from '../ui.js';
import { AlertIcon, CheckIcon, ClockIcon, LockIcon, PlayIcon } from '../icons.js';
import { LessonSheet } from './LessonSheet.js';
import type { SyllabusRow } from '../../../shared/types.js';

interface Data {
  student: { id: number; name: string; courseName: string; regulation: string; stage: number };
  totals: { total: number; dual: number; solo: number; ground: number; night: number; instrument: number };
  syllabus: SyllabusRow[];
  endorsements: { id: number; label: string; reference: string; issuedOn: string; expiresOn: string | null; expired: boolean }[];
  gates: boolean;
}

/** The student's own view of the approved course: what is done, next and locked. */
export function PortalSyllabus({ studentId }: { studentId: number }): JSX.Element {
  const { data, error } = useQuery<Data>(`/students/${studentId}`);
  const [openLesson, setOpenLesson] = useState<number | null>(null);
  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const sequence = data.syllabus.filter((r) => !r.lesson.isRemedial);
  const done = sequence.filter((r) => r.record?.grade === 'S').length;
  const total = sequence.length;

  // Stages, in course order, so the list reads the way the syllabus is filed.
  const stages: { stage: number; rows: SyllabusRow[] }[] = [];
  for (const row of data.syllabus) {
    let g = stages.find((x) => x.stage === row.lesson.stage);
    if (!g) { g = { stage: row.lesson.stage, rows: [] }; stages.push(g); }
    g.rows.push(row);
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <div className="card">
        <div className="card__body">
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <span>
              <span style={{ display: 'block', fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em' }}>
                {data.student.courseName}
              </span>
              <span className="small muted">
                Part {data.student.regulation} · currently Stage {data.student.stage}
              </span>
            </span>
            <Pill tone="blue">{done} of {total} complete</Pill>
          </div>
          <span className="bar" style={{ height: 8 }}>
            <span style={{ width: `${Math.round((done / Math.max(1, total)) * 100)}%` }} />
          </span>
        </div>
      </div>

      {stages.map((group) => (
        <List key={group.stage} header={`Stage ${group.stage}`}>
          {group.rows.map((row) => {
            const complete = row.record?.grade === 'S';
            const locked = row.gated;
            const next = row.state === 'Next up';
            const remedial = row.lesson.isRemedial;
            return (
              <Row
                key={row.lesson.id}
                tile={
                  <Tile color={
                    complete ? 'green'
                    : remedial ? (row.state === 'Available' ? 'orange' : 'gray')
                    : locked ? 'gray' : next ? 'blue' : 'gray'
                  }>
                    {complete ? <CheckIcon />
                      : remedial ? <AlertIcon />
                      : locked ? <LockIcon /> : next ? <PlayIcon /> : <ClockIcon />}
                  </Tile>
                }
                title={`${row.lesson.code} · ${row.lesson.name}`}
                sub={
                  row.gateReason ? <span style={{ color: 'var(--sys-red)' }}>{row.gateReason}</span>
                  : row.record?.flownOn ? `Flown ${row.record.flownOn} · ${row.record.flightHours.toFixed(1)} hrs`
                  : remedial ? 'Flown only if a lesson in this stage is unsatisfactory'
                  : row.lesson.isGround ? `${row.lesson.groundHours.toFixed(1)} hrs ground`
                  : `${row.lesson.flightHours.toFixed(1)} hrs flight · ${row.lesson.groundHours.toFixed(1)} ground`
                }
                trailing={<Pill>{row.state}</Pill>}
                onClick={() => setOpenLesson(row.lesson.id)}
                chevron
              />
            );
          })}
        </List>
      ))}

      <List header="Currency and documents">
        {data.endorsements.map((e) => (
          <Row
            key={e.id}
            title={e.label}
            sub={e.reference || undefined}
            value={
              <span style={{ color: e.expired ? 'var(--sys-red)' : undefined }}>
                {e.expiresOn ? (e.expired ? `Expired ${e.expiresOn}` : `To ${e.expiresOn}`) : `On file ${e.issuedOn}`}
              </span>
            }
          />
        ))}
      </List>

      <p className="note" style={{ marginTop: 0 }}>
        {data.gates
          ? 'Under Part 141 a lesson is released when every earlier lesson is satisfactory and the stage check before it has been approved by the chief instructor.'
          : 'Lesson order is a plan, not a gate. Your instructor may reorder lessons to suit the weather and the aircraft.'}
        {' '}Open a lesson to read its objective and completion standard. Dates are reported from the
        records on file — verify against your logbook before flight.
      </p>

      {openLesson && (
        <LessonSheet studentId={studentId} lessonId={openLesson} onClose={() => setOpenLesson(null)} />
      )}
    </div>
  );
}
